// Import react.
import * as React from 'react';
// Import hooks.
import {
	useInputDataSelectionList,
	UseInputDataSelectionListParameters,
	UseInputDataSelectionListResults,
	useInputFoundationTypeSelectionList,
	UseInputFoundationTypeSelectionListResults,
	UseInputFoundationTypeSelectionListParameters,
} from '@app/hooks/view/input';
// Import enumerations
import {
	BuildingFoundationType,
} from '@app/enumerations';
// Import api models.
import {
    PossibleFoundationType,
	PossibleFoundationTypeDetail,
} from '@app/models/api';

interface UseInputFoundationTypeParameters {
	foundationTypeSelectionListInputParameters: UseInputFoundationTypeSelectionListParameters;
	foundationTypeDetailSelectionListInputParameters: UseInputDataSelectionListParameters;
}

interface UseInputFoundationTypeResults {
	setFoundationTypeDetail: (foundationTypeDetail: string | undefined) => void;
	isSelectedFoundationTypeDetailValid: (selectedFoundationType: BuildingFoundationType | undefined, selectedFoundationTypeDetail: string | undefined) => boolean
	isFoundationTypeSelected: () => boolean;
	foundationTypeSelectionListInput: UseInputFoundationTypeSelectionListResults;
	foundationTypeDetailSelectionListInput: UseInputDataSelectionListResults;
}

const useInputFoundationType = (params: UseInputFoundationTypeParameters): UseInputFoundationTypeResults => {

	// #region exported methods

	/**
	 * Sets the foundation type detail, as well as it's correlating foundation type.
	 * @param selectedFoundationTypeDetail
	 */
	const setFoundationTypeDetail = (foundationTypeDetail: string | undefined): void => {
		if (foundationTypeDetail === undefined) {
			foundationTypeSelectionListInput.setInput(undefined);
			foundationTypeDetailSelectionListInput.setInput(undefined);
		}
		else {
			const foundationType: BuildingFoundationType | undefined = getCorrelatingFoundationTypeByFoundationTypeDetail(foundationTypeDetail);
			if (isSelectedFoundationTypeDetailValid(foundationType, foundationTypeDetail)) {
				foundationTypeSelectionListInput.setInput(foundationType);
				foundationTypeDetailSelectionListInput.setInput(foundationTypeDetail);
			}
			else {
				console.error(`Foundation type detail '${foundationTypeDetail}' is invalid.`);
			}
		}
	}

	/**
	 * Validates the selected foundation type detail against the possible foundation types.
	 * @param selectedFoundationType
	 * @param selectedFoundationTypeDetail
	 */
	const isSelectedFoundationTypeDetailValid = (selectedFoundationType: BuildingFoundationType | undefined, selectedFoundationTypeDetail: string | undefined): boolean => {
		return getMatchingPossibleFoundationTypeDetail(selectedFoundationType, selectedFoundationTypeDetail) !== undefined
	}

	/**
	 * Checks to see if a foundation type has been selected. 
	 **/
	const isFoundationTypeSelected = (): boolean => {
		return foundationTypeSelectionListInput.value !== undefined;
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Fetches the correlating building foundation type by the specified detail code.
	 * @param selectedFoundationTypeDetailCode
	 */
	const getCorrelatingFoundationTypeByFoundationTypeDetail = (selectedFoundationTypeDetailCode: string | undefined): BuildingFoundationType | undefined => {
		let selectedFoundationTypeCode: BuildingFoundationType | undefined = undefined;

		if (selectedFoundationTypeDetailCode !== undefined) {
			let selectedFoundationType: PossibleFoundationType | undefined = params.foundationTypeSelectionListInputParameters.possibleFoundationTypes.find((possibleFoundationType: PossibleFoundationType) => {
				let selectedFoundationTypeDetail: PossibleFoundationTypeDetail | undefined = possibleFoundationType.possibleBuildingFoundationDetails.find((possibleFoundationTypeDetail: PossibleFoundationTypeDetail) => {
					return possibleFoundationTypeDetail.code === selectedFoundationTypeDetailCode;
				});
				return selectedFoundationTypeDetail !== undefined;
			});

			selectedFoundationTypeCode = selectedFoundationType !== undefined && selectedFoundationType.code !== null ? selectedFoundationType.code : undefined;
		}

		return selectedFoundationTypeCode;
	}

	/**
	 * Attempts to find a matching possible foundation type and it's details record.
	 */
	const getMatchingPossibleFoundationTypeDetails = (foundationType: BuildingFoundationType | undefined): PossibleFoundationTypeDetail[] => {
		return foundationTypeSelectionListInput.getMatchingPossibleFoundationType(foundationType)?.possibleBuildingFoundationDetails ?? []
	}

	/**
	 * Attempts to find a matching possible foundation type detail.
	 * @param foundationType
	 * @param foundationTypeDetail
	 */
	const getMatchingPossibleFoundationTypeDetail = (foundationType: BuildingFoundationType | undefined, foundationTypeDetail: string | undefined): PossibleFoundationTypeDetail | undefined => {
		let matchingPossibleFoundationTypeDetail: PossibleFoundationTypeDetail | undefined = undefined;
		// Find the matching possible foundation type details.
		let matchingPossibleFoundationTypeDetails: PossibleFoundationTypeDetail[] = getMatchingPossibleFoundationTypeDetails(foundationType);

		if (foundationTypeDetail !== undefined) {
			// Find the matching possible foundation type detail.
			matchingPossibleFoundationTypeDetail = matchingPossibleFoundationTypeDetails.find((possibleFoundationTypeDetail: PossibleFoundationTypeDetail) => {
				return possibleFoundationTypeDetail.code !== null && possibleFoundationTypeDetail.code === foundationTypeDetail;
			});
		}
		return matchingPossibleFoundationTypeDetail;
	}

	// #endregion private methods


	// #region variables

	// Instantiate the 'foundation type selection list' input.
	const foundationTypeSelectionListInput: UseInputFoundationTypeSelectionListResults = useInputFoundationTypeSelectionList({
		...params.foundationTypeSelectionListInputParameters,
		value: getCorrelatingFoundationTypeByFoundationTypeDetail(params.foundationTypeDetailSelectionListInputParameters.value),
	});

	// Instantiate the 'foundation type detail selection list' input.
	const foundationTypeDetailSelectionListInput: UseInputDataSelectionListResults = useInputDataSelectionList({
		...params.foundationTypeDetailSelectionListInputParameters,
		possibleDataSelections: getMatchingPossibleFoundationTypeDetails(foundationTypeSelectionListInput.value),
	});

	// #endregion variables


	// #region life-cycle methods.

	// Called when the foundation type is updated.
	React.useEffect(() => {
		// Do not make any changes to the detail record, unless the foundation type has changes.
		if (foundationTypeSelectionListInput.doesInputHaveChanges
			&& !isSelectedFoundationTypeDetailValid(foundationTypeSelectionListInput.value, foundationTypeDetailSelectionListInput.value)) {
			foundationTypeDetailSelectionListInput.setInput(undefined);
		}
	}, [foundationTypeSelectionListInput.value]);

	// #endregion life-cycle methods.


	return {
		setFoundationTypeDetail,
		isSelectedFoundationTypeDetailValid,
		isFoundationTypeSelected,
		foundationTypeSelectionListInput,
		foundationTypeDetailSelectionListInput,
	};
}

// Export parameters.
export {
	UseInputFoundationTypeParameters,
}

// Export results.
export {
	UseInputFoundationTypeResults,
}

export default useInputFoundationType;